<script setup>
import { computed, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisModal from 'dashboard/components-next/LaisModal.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import PostSaleTopicAnnotation from './PostSaleTopicAnnotation.vue';
import PostSaleTopicDelivery from './PostSaleTopicDelivery.vue';
import PostSaleTopicLaudemio from './PostSaleTopicLaudemio.vue';
import PostSaleTopicOther from './PostSaleTopicOther.vue';
import PostSaleTopicReinforcement from './PostSaleTopicReinforcement.vue';
import PostSaleTopicTaxes from './PostSaleTopicTaxes.vue';

const props = defineProps({
  catalog: { type: Object, default: () => ({}) },
  saving: { type: Boolean, default: false },
  selectedTopic: { type: Object, default: null },
  show: { type: Boolean, default: false },
});

const emit = defineEmits(['close', 'requestCorrection', 'submit']);
const { t } = useI18n();
const form = reactive({ topic_code: '', attributes: {} });
const error = reactive({ message: '' });

const isEditing = computed(() => Boolean(props.selectedTopic));
const title = computed(() =>
  isEditing.value
    ? t('POST_SALE.TOPICS.FORM.EDIT_TITLE')
    : t('POST_SALE.TOPICS.FORM.CREATE_TITLE')
);
const componentFor = computed(
  () =>
    ({
      averbacao: PostSaleTopicAnnotation,
      entrega_chaves: PostSaleTopicDelivery,
      laudemio: PostSaleTopicLaudemio,
      taxas: PostSaleTopicTaxes,
      reforco_sinal: PostSaleTopicReinforcement,
      outros: PostSaleTopicOther,
    })[form.topic_code]
);

const defaultAttributes = topicCode => {
  if (topicCode === 'averbacao') return { sellers: [] };
  if (topicCode === 'entrega_chaves')
    return {
      payment_method: props.catalog.property?.payment_method || '',
      responsible_seller_id: '',
      bank_id: '',
    };
  if (topicCode === 'laudemio') return { observation: '' };
  if (topicCode === 'taxas') return { tax_type_ids: [] };
  if (topicCode === 'reforco_sinal') return { moment: '', observation: '' };
  if (topicCode === 'outros') return { subject: '', observation: '' };
  return {};
};

const reset = () => {
  form.topic_code = props.selectedTopic?.topic_code || '';
  form.attributes = {
    ...defaultAttributes(form.topic_code),
    ...(props.selectedTopic?.data || {}),
  };
  error.message = '';
};

watch(() => [props.selectedTopic, props.show], reset, { immediate: true });

const selectTopic = topicCode => {
  if (isEditing.value) return;
  form.topic_code = topicCode;
  form.attributes = defaultAttributes(topicCode);
  error.message = '';
};

const submit = () => {
  if (!form.topic_code) {
    error.message = t('POST_SALE.TOPICS.FORM.TOPIC_REQUIRED');
    return;
  }

  emit('submit', { topic_code: form.topic_code, attributes: form.attributes });
};
</script>

<template>
  <LaisModal
    :show="show"
    :title="title"
    max-width-class="max-w-[720px]"
    @close="emit('close')"
  >
    <div class="grid gap-5">
      <label class="grid gap-1 text-sm font-semibold text-lais-text-body">
        {{ t('POST_SALE.TOPICS.FORM.TOPIC') }}
        <select
          :value="form.topic_code"
          class="min-h-10 rounded-lais border border-lais-border bg-white px-2 font-normal disabled:bg-lais-bg-soft"
          :disabled="isEditing"
          @change="selectTopic($event.target.value)"
        >
          <option value="">{{ t('POST_SALE.TOPICS.COMMON.SELECT') }}</option>
          <option
            v-for="option in catalog.topics || []"
            :key="option.id"
            :value="option.id"
          >
            {{ option.label }}
          </option>
        </select>
      </label>
      <p v-if="error.message" class="text-sm text-lais-danger">
        {{ error.message }}
      </p>
      <component
        :is="componentFor"
        v-if="componentFor"
        :catalog="catalog"
        :value="form.attributes"
        @request-correction="
          emit('requestCorrection', {
            topic_code: form.topic_code,
            attributes: form.attributes,
          })
        "
        @update:value="form.attributes = $event"
      />
    </div>
    <template #footer>
      <div class="flex justify-end gap-3">
        <LaisPrimaryButton variant="ghost" @click="emit('close')">
          {{ t('POST_SALE.ACTIONS.CANCEL') }}
        </LaisPrimaryButton>
        <LaisPrimaryButton :is-loading="saving" @click="submit">
          {{
            isEditing
              ? t('POST_SALE.TOPICS.ACTIONS.SAVE_EDIT')
              : t('POST_SALE.TOPICS.ACTIONS.ADD')
          }}
        </LaisPrimaryButton>
      </div>
    </template>
  </LaisModal>
</template>
